import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import SuggestedBlogPosts from "Components/SuggestedBlogPosts";
import styles from "Styles/components/CompletionPage.module.css";
import { useFormData } from "Contexts/FormDataContext";
import { useSession } from "Contexts/SessionContext";
import useBackgroundUpdate from "Hooks/useBackgroundUpdate";
import SessionLoading from "Components/SessionLoading";
import { Button } from "Components/Button";
import {
  q4ScoreMapping,
  q7MaturityMapping,
  nurturingApproachData,
  topGoalsData,
  marketingPainPointsData,
  suggestedBlogPostsData,
  featuredPostData,
} from "Data";
import {
  updateRequestFullInsightsReport,
  getSessionWithContact,
} from "Lib/session-utils";
import blogFeaturedImage from "Assets/blog/featured-post.png";
import { submitToHubSpot, formatFieldsForHubSpot } from "Lib/hubspot-utils";

const CompletionPage = () => {
  const [searchParams] = useSearchParams();
  const [showThankYou, setShowThankYou] = useState(false);
  const [showDuplicateRequest, setShowDuplicateRequest] = useState(false);
  const [isRequesting, setIsRequesting] = useState(false);
  const [hasRequestedReport, setHasRequestedReport] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [contactEmail, setContactEmail] = useState("");

  useBackgroundUpdate("hidden");

  const {
    currentFormData: {
      companyName,
      q4Score,
      q7Score,
      nurturingApproach,
      topMarketingGoals,
      marketingPainPoints,
    },
    restoreFromSession,
  } = useFormData();

  const { sessionCode: contextSessionCode } = useSession();
  const sessionCode = searchParams.get("session") || contextSessionCode;

  useEffect(() => {
    const loadSessionData = async () => {
      if (sessionCode) {
        const result = await getSessionWithContact(sessionCode);
        if (result.success && result.data) {
          const { contact, responses } = result.data;

          const restoredData = {
            userFirstName: contact?.firstname,
            userLastName: contact?.lastname,
            userEmail: contact?.email,
            companyName: contact?.company,
            industry: contact?.industry,
            companySize: contact?.numemployees,
            q4Score:
              result.data.q4_score ||
              responses?.q4_marketing_channels?.score ||
              0,
            q7Score:
              result.data.q7_score || responses?.q7_capabilities?.score || 0,
            primaryMarketingChannels:
              responses?.q4_marketing_channels?.channels || [],
            nurturingApproach: responses?.q5_nurturing?.approach,
            marketingStaffing: responses?.q6_staffing?.staffing,
            marketingCapabilities:
              responses?.q7_capabilities?.capabilities || [],
            topMarketingGoals: responses?.q8_goals?.goals || [],
            marketingPainPoints: responses?.q9_pain_points?.painPoints || [],
          };

          restoreFromSession(restoredData);
          setContactEmail(contact?.email || "");
          setHasRequestedReport(
            result.data.request_full_insights_report === true,
          );
        }
      }
      setIsLoading(false);
    };

    loadSessionData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionCode]);

  const getQ4Display = (score) => {
    const mapping = q4ScoreMapping.find(
      (m) => score >= m.min && score <= m.max,
    );
    return mapping?.display || "單點起步型";
  };

  const getQ5Display = (approach) => {
    const option = nurturingApproachData.find((o) => o.value === approach);
    return option?.displayLabel || "人工跟進";
  };

  const getQ7Maturity = (score) => {
    const mapping = q7MaturityMapping.find(
      (m) => score >= m.min && score <= m.max,
    );
    return mapping || q7MaturityMapping[0];
  };

  const getGoalLabels = (goals) => {
    if (!goals || goals.length === 0) return [];
    return goals.map((goalValue) => {
      const goal = topGoalsData.find((g) => g.value === goalValue);
      return goal?.label || goalValue;
    });
  };

  const getPainPointLabels = (painPoints) => {
    if (!painPoints || painPoints.length === 0) return [];
    return painPoints.map((ppValue) => {
      const pp = marketingPainPointsData.find((p) => p.value === ppValue);
      const label = pp?.label || ppValue;
      const [title, ...descriptionParts] = label.split("\n");
      return {
        title: title || "",
        description: descriptionParts.join("\n") || "",
      };
    });
  };

  const handleRequestReport = async () => {
    if (isRequesting) {
      return;
    }

    if (hasRequestedReport) {
      setShowDuplicateRequest(true);
      return;
    }

    setIsRequesting(true);

    try {
      if (sessionCode) {
        const updateResult = await updateRequestFullInsightsReport(sessionCode, contactEmail);

        if (updateResult.alreadySet === true || updateResult.yearLimitReached === true) {
          setHasRequestedReport(true);
          setShowDuplicateRequest(true);
          setIsRequesting(false);
          return;
        }

        if (!updateResult.success) {
          console.error("Error updating report request:", updateResult.error);
          setIsRequesting(false);
          return;
        }

        const sessionResult = await getSessionWithContact(sessionCode);
        if (sessionResult.success && sessionResult.data?.contact) {
          const { contact } = sessionResult.data;
          const hubspotData = {
            firstname: contact.firstname,
            lastname: contact.lastname,
            email: contact.email,
            request_full_insights_report: "true",
          };

          console.log(
            "[HubSpot] Submitting request_full_insights_report:",
            hubspotData,
          );

          const fields = formatFieldsForHubSpot(hubspotData);
          const result = await submitToHubSpot(fields, {
            pageName: "Result Page - Request Full Report",
          });

          console.log(
            "[HubSpot] Request full report submission result:",
            result,
          );
        }
      }

      setHasRequestedReport(true);
      setShowThankYou(true);
    } catch (error) {
      console.error("Error requesting report:", error);
    } finally {
      setIsRequesting(false);
    }
  };

  const maturity = getQ7Maturity(q7Score || 0);
  const q4Display = getQ4Display(q4Score || 0);
  const q5Display = getQ5Display(nurturingApproach);
  const goalLabels = getGoalLabels(topMarketingGoals);
  const painPointLabels = getPainPointLabels(marketingPainPoints);

  if (isLoading) {
    return (
      <SessionLoading
        heading="載入中"
        subtext="正在載入你的盤點結果"
        showLogo={false}
      />
    );
  }

  return (
    <div className={styles.completionWrapper}>
      {/* Decorative elements - left and right sides, only for main content */}
      <div className={styles.decorativeElements} aria-hidden="true">
        <motion.svg
          className={`${styles.decoCircle}`}
          animate={{ x: [0, 40, 0], y: [0, 20, 0] }}
          transition={{
            duration: 20,
            repeat: Infinity,
            repeatType: "mirror",
            ease: "easeInOut",
          }}
          width="316"
          height="316"
          viewBox="0 0 316 316"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <circle cx="158" cy="158" r="158" fill="#7DECB9" />
        </motion.svg>
        <motion.svg
          className={`${styles.decoHorseshoe}`}
          animate={{ x: [0, -30, 0], y: [0, 30, 0], rotate: [0, 15, 0] }}
          transition={{
            duration: 25,
            repeat: Infinity,
            repeatType: "mirror",
            ease: "easeInOut",
          }}
          width="356"
          height="342"
          viewBox="0 0 356 342"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <g clipPath="url(#clip0_completion_horseshoe)">
            <path
              d="M189.574 272.971C268.122 251.738 314.556 170.852 293.299 92.2939L206.64 115.717C214.942 146.403 196.801 177.992 166.121 186.284C135.457 194.572 103.857 176.431 95.5544 145.744L8.88521 169.166C30.147 247.73 111.043 294.201 189.574 272.971Z"
              fill="#FCCA23"
            />
          </g>
          <defs>
            <clipPath id="clip0_completion_horseshoe">
              <rect
                width="288.507"
                height="208.749"
                fill="white"
                transform="translate(0 176.006) rotate(-37.5937)"
              />
            </clipPath>
          </defs>
        </motion.svg>
        <motion.svg
          className={`${styles.decoRing}`}
          animate={{ x: [0, -20, 0], y: [0, 30, 0], rotate: [0, 180, 0] }}
          transition={{
            duration: 30,
            repeat: Infinity,
            repeatType: "mirror",
            ease: "easeInOut",
          }}
          width="119"
          height="119"
          viewBox="0 0 119 119"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M119 59.5C119 92.3609 92.3609 119 59.5 119C26.6391 119 0 92.3609 0 59.5C0 26.6391 26.6391 0 59.5 0C92.3609 0 119 26.6391 119 59.5ZM16.9283 59.5C16.9283 83.0117 35.9883 102.072 59.5 102.072C83.0117 102.072 102.072 83.0117 102.072 59.5C102.072 35.9883 83.0117 16.9283 59.5 16.9283C35.9883 16.9283 16.9283 35.9883 16.9283 59.5Z"
            fill="#7DECB9"
          />
        </motion.svg>
        <motion.svg
          className={`${styles.decoStar}`}
          animate={{ rotate: [0, -360] }}
          transition={{ duration: 30, repeat: Infinity, ease: "linear" }}
          width="133"
          height="133"
          viewBox="0 0 133 133"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <g clipPath="url(#clip0_completion_star)">
            <path
              d="M91.8236 90.1076L104.098 90.8L99.1445 79.5391L110.935 75.9839L102.431 67.1127L112.313 59.7355L101.281 54.3284L108.059 44.0196L95.8309 42.7221L98.693 30.7254L86.747 33.7008L85.3423 21.4622L75.1134 28.3486L69.6186 17.3441L62.3423 27.3108L53.4134 18.8693L49.9718 30.7159L38.6882 25.8565L39.4913 38.15L27.2138 37.4556L32.1672 48.7165L20.3763 52.2717L28.8807 61.1429L19.0018 68.522L30.0338 73.9292L23.2563 84.238L35.4808 85.5335L32.622 97.5322L44.568 94.5568L45.9727 106.795L56.1983 99.907L61.6964 110.914L68.9694 100.945L77.8983 109.386L81.3431 97.5417L92.6268 102.401L91.8236 90.1076Z"
              fill="#1754E7"
            />
          </g>
          <defs>
            <clipPath id="clip0_completion_star">
              <rect
                width="96.3363"
                height="96.3363"
                fill="white"
                transform="translate(49.8867) rotate(31.1877)"
              />
            </clipPath>
          </defs>
        </motion.svg>
      </div>
      <div className={styles.container}>
        <header className={styles.header}>
          <h1 className={styles.title}>
            {companyName || "您的"} 2026 行銷現況盤點總覽
          </h1>
          <p className={styles.subtitle}>
            想把結果變成更具體的行動計劃嗎？盤點只是起點，
            <br />
            解決卡關需要更完整的策略佈局。
          </p>
          <a href="#request-report" className={styles.linkButton}>
            立即索取完整洞察報告（每人僅限申請一次）
          </a>
          <p className={styles.note}>
            3 個工作天內我們將針對您的行銷階段與痛點，打造一份可落地的成長藍圖。
          </p>
        </header>

        <section className={styles.maturitySection}>
          <span className={styles.badge}>行銷成熟度階段</span>
          <div className={styles.maturityCard}>
            <h2 className={styles.maturityTitle}>{maturity.title}</h2>
            <div className={styles.maturityContent}>
              <h3 className={styles.maturitySubtitle}>{maturity.subtitle}</h3>
              <p className={styles.maturityDescription}>
                {maturity.description}
              </p>
            </div>
          </div>
        </section>

        <div className={styles.twoColumnSection}>
          <div className={styles.column}>
            <span className={styles.badge}>行銷管道佈局</span>
            <div className={styles.card}>
              <h3 className={styles.cardTitle}>{q4Display}</h3>
            </div>
          </div>
          <div className={styles.column}>
            <span className={styles.badge}>名單跟進與培育</span>
            <div className={styles.card}>
              <div className={styles.iconWrapper}>🔧</div>
              <h3 className={styles.cardTitle}>{q5Display}</h3>
            </div>
          </div>
        </div>

        {goalLabels.length > 0 && (
          <section className={styles.goalsSection}>
            <span className={styles.badge}>三個優先實現目標</span>
            <div className={styles.goalsList}>
              {goalLabels.map((label, index) => (
                <div key={index} className={styles.goalItem}>
                  <span className={styles.goalText}>{label}</span>
                </div>
              ))}
            </div>
          </section>
        )}

        {painPointLabels.length > 0 && (
          <section className={styles.bottleneckSection}>
            <span className={styles.badge}>目前主要成長瓶頸</span>
            <div className={styles.bottleneckGrid}>
              {painPointLabels.map((item, index) => (
                <div key={index} className={styles.bottleneckCard}>
                  <h4 className={styles.bottleneckTitle}>{item.title}</h4>
                  <p className={styles.bottleneckText}>{item.description}</p>
                </div>
              ))}
            </div>
          </section>
        )}

        <section className={styles.ctaSection} id="request-report">
          <Button
            type="action"
            clickCB={handleRequestReport}
            disabled={isRequesting}
            className={styles.ctaButton}
          >
            {isRequesting ? "處理中..." : "索取完整洞察報告"}
          </Button>
        </section>

        {showThankYou && (
          <div className={styles.modalOverlay}>
            <div className={styles.modal}>
              <h3 className={styles.modalTitle}>Thank you!</h3>
              <p className={styles.modalText}>
                已收到您的申請！專屬洞察報告正在為您製作中。預計於 3
                個工作天內寄送至您所填寫的電子信箱。
              </p>
              <p className={styles.modalText}>請留意 Email，以免錯過通知。</p>
              <Button
                className={styles.closeModal}
                type="action"
                clickCB={() => setShowThankYou(false)}
              >
                關閉
              </Button>
              <svg
                style={{
                  position: "absolute",
                  bottom: "0",
                  left: "0",
                  borderBottomLeftRadius: "12px",
                }}
                xmlns="http://www.w3.org/2000/svg"
                width="215"
                height="192"
                viewBox="0 0 215 192"
                fill="none"
              >
                <path
                  d="M7.85699 206.588C72.4382 256.084 164.897 243.833 214.386 179.226L143.138 124.617C123.806 149.854 87.6932 154.632 62.4699 135.298C37.2586 115.975 32.4735 79.8538 51.8056 54.6176L-19.4478 -3.93739e-05C-68.9393 64.6141 -56.7121 157.103 7.85699 206.588Z"
                  fill="#FD8A8A"
                />
              </svg>
              <svg
                style={{ position: "absolute", bottom: "20px", right: "20px" }}
                xmlns="http://www.w3.org/2000/svg"
                width="94"
                height="94"
                viewBox="0 0 94 94"
                fill="none"
              >
                <path
                  d="M72.8217 72.7636L85.0959 73.4561L80.1425 62.1951L91.9335 58.6399L83.429 49.7687L93.3112 42.3916L82.2792 36.9844L89.0567 26.6756L76.829 25.3781L79.691 13.3814L67.745 16.3568L66.3403 4.11824L56.1115 11.0046L50.6166 6.03837e-05L43.3403 9.96679L34.4115 1.52535L30.9699 13.3719L19.6863 8.51247L20.4894 20.806L8.21186 20.1116L13.1653 31.3725L1.37434 34.9277L9.87876 43.7989L-0.000152694 51.178L11.0318 56.5852L4.25438 66.894L16.4788 68.1895L13.62 80.1882L25.566 77.2128L26.9707 89.4514L37.1963 82.563L42.6945 93.5695L49.9675 83.6008L58.8963 92.0423L62.3412 80.1977L73.6248 85.0571L72.8217 72.7636Z"
                  fill="#FCCA23"
                />
              </svg>
            </div>
          </div>
        )}

        {showDuplicateRequest && (
          <div className={styles.modalOverlay}>
            <div className={styles.modal}>
              <h3 className={styles.modalTitle}>Oops!</h3>
              <p className={styles.modalText}>您已申請過洞察報告囉。</p>
              <p className={styles.modalText}>
                每位用戶僅限申請一次，請留意信箱中的報告通知。
              </p>
              <Button
                className={styles.closeModal}
                type="action"
                clickCB={() => setShowDuplicateRequest(false)}
              >
                關閉
              </Button>
              <svg
                style={{
                  position: "absolute",
                  bottom: "0",
                  left: "0",
                  borderBottomLeftRadius: "12px",
                }}
                xmlns="http://www.w3.org/2000/svg"
                width="215"
                height="192"
                viewBox="0 0 215 192"
                fill="none"
              >
                <path
                  d="M7.85699 206.588C72.4382 256.084 164.897 243.833 214.386 179.226L143.138 124.617C123.806 149.854 87.6932 154.632 62.4699 135.298C37.2586 115.975 32.4735 79.8538 51.8056 54.6176L-19.4478 -3.93739e-05C-68.9393 64.6141 -56.7121 157.103 7.85699 206.588Z"
                  fill="#FD8A8A"
                />
              </svg>
              <svg
                style={{ position: "absolute", bottom: "20px", right: "20px" }}
                xmlns="http://www.w3.org/2000/svg"
                width="94"
                height="94"
                viewBox="0 0 94 94"
                fill="none"
              >
                <path
                  d="M72.8217 72.7636L85.0959 73.4561L80.1425 62.1951L91.9335 58.6399L83.429 49.7687L93.3112 42.3916L82.2792 36.9844L89.0567 26.6756L76.829 25.3781L79.691 13.3814L67.745 16.3568L66.3403 4.11824L56.1115 11.0046L50.6166 6.03837e-05L43.3403 9.96679L34.4115 1.52535L30.9699 13.3719L19.6863 8.51247L20.4894 20.806L8.21186 20.1116L13.1653 31.3725L1.37434 34.9277L9.87876 43.7989L-0.000152694 51.178L11.0318 56.5852L4.25438 66.894L16.4788 68.1895L13.62 80.1882L25.566 77.2128L26.9707 89.4514L37.1963 82.563L42.6945 93.5695L49.9675 83.6008L58.8963 92.0423L62.3412 80.1977L73.6248 85.0571L72.8217 72.7636Z"
                  fill="#FCCA23"
                />
              </svg>
            </div>
          </div>
        )}
      </div>
      <SuggestedBlogPosts
        title="最新數位行銷趨勢"
        links={suggestedBlogPostsData}
      />

      <section className={styles.featuredPostSection}>
        <div className={styles.noisyCircleContainer}>
          <svg
            width="100%"
            height="100%"
            viewBox="0 0 1000 1000"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <defs>
              <radialGradient
                id="featured-circle-gradient"
                spreadMethod="repeat"
              >
                <stop offset="0" stopColor="#1754E7" />
                <stop offset="0.5" stopColor="#2B69FF">
                  <animate
                    attributeName="stopColor"
                    values="#1754E7; #2B69FF; #1754E7;"
                    dur="4s"
                    repeatCount="indefinite"
                  />
                  <animate
                    attributeName="offset"
                    values="0.5; 1; 0.5; 0; 0.5;"
                    dur="10s"
                    repeatCount="indefinite"
                  />
                </stop>
                <stop offset="1" stopColor="#1754E7" />
              </radialGradient>
            </defs>
            <circle
              fill="url(#featured-circle-gradient)"
              cx="500"
              cy="500"
              r="500"
            />
          </svg>
          <div className={styles.noisyCircleOverlay}>
            <svg
              width="100%"
              height="100%"
              viewBox="0 0 1000 1000"
              xmlns="http://www.w3.org/2000/svg"
            >
              <filter id="featuredNoiseFilter">
                <feTurbulence
                  type="fractalNoise"
                  baseFrequency="0.65"
                  numOctaves="3"
                  stitchTiles="stitch"
                >
                  <animate
                    attributeName="baseFrequency"
                    values="0.65; 0.6"
                    dur="10s"
                    repeatCount="indefinite"
                  />
                  <animate
                    attributeName="numOctaves"
                    from="1"
                    to="5"
                    dur="10s"
                    repeatCount="indefinite"
                  />
                </feTurbulence>
              </filter>
              <rect
                width="100%"
                height="100%"
                filter="url(#featuredNoiseFilter)"
              />
            </svg>
          </div>
          <div className={styles.noisyCircleOverlay2} />
        </div>
        <div className={styles.noisyCircleContainerRight}>
          <svg
            width="100%"
            height="100%"
            viewBox="0 0 1000 1000"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <defs>
              <radialGradient
                id="featured-circle-gradient-right"
                spreadMethod="repeat"
              >
                <stop offset="0" stopColor="#1754E7" />
                <stop offset="0.5" stopColor="#2B69FF">
                  <animate
                    attributeName="stopColor"
                    values="#1754E7; #2B69FF; #1754E7;"
                    dur="4s"
                    repeatCount="indefinite"
                  />
                  <animate
                    attributeName="offset"
                    values="0.5; 1; 0.5; 0; 0.5;"
                    dur="10s"
                    repeatCount="indefinite"
                  />
                </stop>
                <stop offset="1" stopColor="#1754E7" />
              </radialGradient>
            </defs>
            <circle
              fill="url(#featured-circle-gradient-right)"
              cx="500"
              cy="500"
              r="500"
            />
          </svg>
          <div className={styles.noisyCircleOverlay}>
            <svg
              width="100%"
              height="100%"
              viewBox="0 0 1000 1000"
              xmlns="http://www.w3.org/2000/svg"
            >
              <filter id="featuredNoiseFilterRight">
                <feTurbulence
                  type="fractalNoise"
                  baseFrequency="0.65"
                  numOctaves="3"
                  stitchTiles="stitch"
                >
                  <animate
                    attributeName="baseFrequency"
                    values="0.65; 0.6"
                    dur="10s"
                    repeatCount="indefinite"
                  />
                  <animate
                    attributeName="numOctaves"
                    from="1"
                    to="5"
                    dur="10s"
                    repeatCount="indefinite"
                  />
                </feTurbulence>
              </filter>
              <rect
                width="100%"
                height="100%"
                filter="url(#featuredNoiseFilterRight)"
              />
            </svg>
          </div>
          <div className={styles.noisyCircleOverlay2} />
        </div>
        <div className={styles.labelBtn}>
          <span className={styles.labelBtnText}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="35"
              height="36"
              viewBox="0 0 35 36"
              fill="none"
              style={{ position: "absolute", top: "-30px", left: "-40px" }}
            >
              <path
                d="M7.57196 10.61C7.56421 10.2311 7.54871 9.47323 7.92472 9.08648C8.67674 8.313 9.82802 8.28959 10.611 9.03184L26.2713 23.877C27.0543 24.6193 27.0775 25.7561 26.3255 26.5296C25.5735 27.3031 24.4222 27.3265 23.6392 26.5842L7.97895 11.739C7.59519 11.7468 7.5797 10.989 7.57196 10.61Z"
                fill="url(#paint0_linear_5062_750)"
              />
              <path
                d="M0.039702 33.6194C0.0152352 32.4228 0.765432 31.6094 1.91519 31.586L21.0779 31.1962C22.2276 31.1729 23.0105 31.955 23.0349 33.1517C23.0594 34.3483 22.3092 35.1617 21.1594 35.185L1.99675 35.5748C0.846988 35.5982 0.0641688 34.816 0.039702 33.6194Z"
                fill="url(#paint1_linear_5062_750)"
              />
              <path
                d="M30.7901 21.1056L30.3993 1.99266C30.3758 0.845884 31.16 0.0650962 32.3598 0.0406928C33.5595 0.0162895 34.375 0.76454 34.3985 1.91132L34.7892 21.0243C34.8127 22.1711 34.0285 22.9519 32.8287 22.9763C31.629 23.0007 30.8135 22.2524 30.7901 21.1056Z"
                fill="url(#paint2_linear_5062_750)"
              />
              <defs>
                <linearGradient
                  id="paint0_linear_5062_750"
                  x1="6.89788"
                  y1="8.81168"
                  x2="26.8018"
                  y2="26.8003"
                  gradientUnits="userSpaceOnUse"
                >
                  <stop stop-color="#F1F7FE" stop-opacity="0" />
                  <stop
                    offset="0.006"
                    stop-color="#EDF5FE"
                    stop-opacity="0.012"
                  />
                  <stop
                    offset="0.104"
                    stop-color="#B8D5FA"
                    stop-opacity="0.2"
                  />
                  <stop
                    offset="0.2"
                    stop-color="#8BBAF6"
                    stop-opacity="0.384"
                  />
                  <stop
                    offset="0.291"
                    stop-color="#69A5F3"
                    stop-opacity="0.559"
                  />
                  <stop
                    offset="0.377"
                    stop-color="#5096F2"
                    stop-opacity="0.725"
                  />
                  <stop
                    offset="0.456"
                    stop-color="#418DF0"
                    stop-opacity="0.876"
                  />
                  <stop offset="0.52" stop-color="#3C8AF0" />
                  <stop
                    offset="0.585"
                    stop-color="#408DF0"
                    stop-opacity="0.864"
                  />
                  <stop
                    offset="0.657"
                    stop-color="#4E95F1"
                    stop-opacity="0.715"
                  />
                  <stop
                    offset="0.732"
                    stop-color="#64A2F3"
                    stop-opacity="0.559"
                  />
                  <stop
                    offset="0.808"
                    stop-color="#82B4F5"
                    stop-opacity="0.399"
                  />
                  <stop
                    offset="0.887"
                    stop-color="#AACCF8"
                    stop-opacity="0.236"
                  />
                  <stop
                    offset="0.965"
                    stop-color="#DAE9FC"
                    stop-opacity="0.072"
                  />
                  <stop offset="1" stop-color="#F1F7FE" stop-opacity="0" />
                </linearGradient>
                <linearGradient
                  id="paint1_linear_5062_750"
                  x1="23.7899"
                  y1="33.1363"
                  x2="-0.427815"
                  y2="33.6315"
                  gradientUnits="userSpaceOnUse"
                >
                  <stop stop-color="#F1F7FE" stop-opacity="0" />
                  <stop
                    offset="0.006"
                    stop-color="#EDF5FE"
                    stop-opacity="0.012"
                  />
                  <stop
                    offset="0.104"
                    stop-color="#B8D5FA"
                    stop-opacity="0.2"
                  />
                  <stop
                    offset="0.2"
                    stop-color="#8BBAF6"
                    stop-opacity="0.384"
                  />
                  <stop
                    offset="0.291"
                    stop-color="#69A5F3"
                    stop-opacity="0.559"
                  />
                  <stop
                    offset="0.377"
                    stop-color="#5096F2"
                    stop-opacity="0.725"
                  />
                  <stop
                    offset="0.456"
                    stop-color="#418DF0"
                    stop-opacity="0.876"
                  />
                  <stop offset="0.52" stop-color="#3C8AF0" />
                  <stop
                    offset="0.585"
                    stop-color="#408DF0"
                    stop-opacity="0.864"
                  />
                  <stop
                    offset="0.657"
                    stop-color="#4E95F1"
                    stop-opacity="0.715"
                  />
                  <stop
                    offset="0.732"
                    stop-color="#64A2F3"
                    stop-opacity="0.559"
                  />
                  <stop
                    offset="0.808"
                    stop-color="#82B4F5"
                    stop-opacity="0.399"
                  />
                  <stop
                    offset="0.887"
                    stop-color="#AACCF8"
                    stop-opacity="0.236"
                  />
                  <stop
                    offset="0.965"
                    stop-color="#DAE9FC"
                    stop-opacity="0.072"
                  />
                  <stop offset="1" stop-color="#F1F7FE" stop-opacity="0" />
                </linearGradient>
                <linearGradient
                  id="paint2_linear_5062_750"
                  x1="32.3598"
                  y1="0.0406928"
                  x2="32.8263"
                  y2="22.9763"
                  gradientUnits="userSpaceOnUse"
                >
                  <stop stop-color="#F1F7FE" stop-opacity="0" />
                  <stop
                    offset="0.006"
                    stop-color="#EDF5FE"
                    stop-opacity="0.012"
                  />
                  <stop
                    offset="0.104"
                    stop-color="#B8D5FA"
                    stop-opacity="0.2"
                  />
                  <stop
                    offset="0.2"
                    stop-color="#8BBAF6"
                    stop-opacity="0.384"
                  />
                  <stop
                    offset="0.291"
                    stop-color="#69A5F3"
                    stop-opacity="0.559"
                  />
                  <stop
                    offset="0.377"
                    stop-color="#5096F2"
                    stop-opacity="0.725"
                  />
                  <stop
                    offset="0.456"
                    stop-color="#418DF0"
                    stop-opacity="0.876"
                  />
                  <stop offset="0.52" stop-color="#3C8AF0" />
                  <stop
                    offset="0.585"
                    stop-color="#408DF0"
                    stop-opacity="0.864"
                  />
                  <stop
                    offset="0.657"
                    stop-color="#4E95F1"
                    stop-opacity="0.715"
                  />
                  <stop
                    offset="0.732"
                    stop-color="#64A2F3"
                    stop-opacity="0.559"
                  />
                  <stop
                    offset="0.808"
                    stop-color="#82B4F5"
                    stop-opacity="0.399"
                  />
                  <stop
                    offset="0.887"
                    stop-color="#AACCF8"
                    stop-opacity="0.236"
                  />
                  <stop
                    offset="0.965"
                    stop-color="#DAE9FC"
                    stop-opacity="0.072"
                  />
                  <stop offset="1" stop-color="#F1F7FE" stop-opacity="0" />
                </linearGradient>
              </defs>
            </svg>{" "}
            see also...
          </span>
        </div>
        <a
          href={featuredPostData.url}
          target="_blank"
          rel="noopener noreferrer"
          className={styles.featuredPostCard}
        >
          <img src={blogFeaturedImage} alt="HubSpot 中文版 同步上線！" />
          <div className={styles.featuredPostContent}>
            <h3 className={styles.featuredPostTitle}>
              {featuredPostData.title}
            </h3>
            <p className={styles.featuredPostDescription}>
              {featuredPostData.description}
            </p>
            <span className={styles.featuredPostCta}>立即瞭解</span>
          </div>
        </a>
        <footer className={styles.footer}>
          <p className={styles.footerText}>© 2026 hububble.co</p>
          <a
            href="https://www.hububble.co/privacy"
            target="_blank"
            rel="noopener noreferrer"
            className={styles.footerLink}
          >
            Privacy & Terms
          </a>
        </footer>
      </section>
    </div>
  );
};

export default CompletionPage;
